import { EFinancingProgram, getEFinancingProgramList } from '@if/core/types'
import * as yup from '@if/core/yup'
import {
  ControlledMultipleSelect,
  FormBody,
  FormContainerProvider,
  FormSubmitButton,
  useConfirmDialog,
  useSnackbar,
} from '@if/mui'
import { useRemoveLoansByProgram } from '@src/api/admin-commands-api'
import { DevToolsCommandType } from '@src/types/dev-tools'
import { Control, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CommandCard from './CommandCard'

/**
 * The "select all" entry. It is not an EFinancingProgram value: it rides along in the
 * selection purely so its own row shows as checked, and is stripped before the command runs.
 */
const ALL_PROGRAMS = 'all'

const schema = yup.object({
  financingProgramIds: yup.array().of(yup.text().required()).required().min(1).default([]),
})

type CleanLoansDraft = yup.InferDraft<typeof schema>
type CleanLoansValues = yup.InferSubmit<typeof schema>
type CleanLoansOutcome = Promise<void | false>

const programsOf = (selected: string[]) => selected.filter((value) => value !== ALL_PROGRAMS)

interface ProgramSelectProps {
  control: Control<CleanLoansDraft, unknown, CleanLoansValues>
}

/**
 * The program picker, in its own component so it can read the selection it is about to
 * replace: the raw value alone cannot say whether "All" or a program was the row just
 * clicked, and the two want opposite outcomes.
 */
const ProgramSelect = ({ control }: ProgramSelectProps) => {
  const { t } = useTranslation()
  const allPrograms = getEFinancingProgramList()
  const selected = useWatch({ control, name: 'financingProgramIds' }) ?? []

  const options = [
    { label: t('admin.devTools.commands.removeAllLoanByProgram.allOption'), value: ALL_PROGRAMS },
    ...allPrograms.map((program) => ({
      label: t(`EFinancingProgram.${program}`, { ns: 'enum' }),
      value: String(program),
    })),
  ]

  const resolveSelection = (next: string[]) => {
    const wasAll = selected.includes(ALL_PROGRAMS)
    if (wasAll !== next.includes(ALL_PROGRAMS)) {
      return wasAll ? [] : [ALL_PROGRAMS, ...allPrograms.map(String)]
    }
    const programs = programsOf(next)
    return programs.length === allPrograms.length ? [ALL_PROGRAMS, ...programs] : programs
  }

  return (
    <ControlledMultipleSelect
      control={control}
      name="financingProgramIds"
      label={t('admin.devTools.commands.removeAllLoanByProgram.inputLabel')}
      options={options}
      parseValue={resolveSelection}
      renderSelectedLabel={(picked) =>
        picked.some((option) => option.value === ALL_PROGRAMS)
          ? t('admin.devTools.commands.removeAllLoanByProgram.allOption')
          : picked.map((option) => option.label).join(', ')
      }
      required
    />
  )
}

/**
 * Purge every loan of the selected financing programs. The confirmation names the programs
 * it is about to delete, and a failure is reported the way a success is — in a single
 * snackbar — rather than as an inline form error.
 */
const CleanLoansByProgramCard = () => {
  const { t } = useTranslation()
  const { showError } = useSnackbar()
  const removeLoansByProgram = useRemoveLoansByProgram()

  const allPrograms = getEFinancingProgramList()
  const labelOf = (programId: string) => t(`EFinancingProgram.${programId as EFinancingProgram}`, { ns: 'enum' })

  const confirm = useConfirmDialog<{ programIds: string[] }>({
    title: t('admin.devTools.dialog.title'),
    message: ({ props }) =>
      props.programIds.length === allPrograms.length
        ? t('admin.devTools.commands.removeAllLoanByProgram.confirmationMessage')
        : t('admin.devTools.commands.removeAllLoanByProgram.confirmationMessageProgram', {
            program: props.programIds.map(labelOf).join(', '),
          }),
    confirmLabel: t('admin.devTools.commands.removeAllLoanByProgram.actionLabel'),
    mode: 'destructive',
  })

  const handleSubmit = async (values: CleanLoansValues): CleanLoansOutcome => {
    const financingProgramIds = programsOf(values.financingProgramIds)
    if (!(await confirm.open({ programIds: financingProgramIds }))) return false
    try {
      await removeLoansByProgram.mutateAsync({ financingProgramIds })
    } catch {
      showError(t('admin.devTools.messages.error'))
      return false
    }
  }

  return (
    <FormContainerProvider<CleanLoansDraft, unknown, CleanLoansOutcome, CleanLoansValues>
      mode="delete"
      useFormSetup={() => ({ schema })}
      onSubmit={handleSubmit}
      successMessage={t('admin.devTools.messages.success')}
    >
      <CommandCard
        title={t('admin.devTools.commands.removeAllLoanByProgram.title')}
        description={t('admin.devTools.commands.removeAllLoanByProgram.description')}
        commandType={DevToolsCommandType.RemoveAllLoanByProgram}
        actions={<FormSubmitButton label={t('admin.devTools.commands.removeAllLoanByProgram.actionLabel')} />}
      >
        <FormBody<CleanLoansDraft, unknown, CleanLoansValues>>
          {({ control }) => <ProgramSelect control={control} />}
        </FormBody>
      </CommandCard>
    </FormContainerProvider>
  )
}

export default CleanLoansByProgramCard
